// services/chat/chatService.ts
import { useEffect, useRef } from 'react'

import type { Channel } from 'pusher-js'
import type { QueryClient } from 'react-query'

import { ensurePusher } from 'services/pusherclient/pusherClient'

type UseChatServiceParams = {
  token: string | null
  conversationId: string | null
  queryClient: QueryClient
  currentUserId?: string | null
  enabled?: boolean
}

function getSenderId(payload: any, msg: any): string | null {
  const senderId =
    msg?.sender_id ??
    msg?.user_id ??
    payload?.sender?.id ??
    null

  if (senderId == null) return null

  const s = String(senderId)

  if (!s || s === '0') return null

  return s
}

export function useChatService({
  token,
  conversationId,
  queryClient,
  currentUserId = null,
  enabled = true,
}: UseChatServiceParams) {
  const channelRef = useRef<Channel | null>(null)
  const userChannelRef = useRef<Channel | null>(null)

  const currentUserIdRef = useRef<string | null>(
    currentUserId != null
      ? String(currentUserId)
      : null
  )

  useEffect(() => {
    currentUserIdRef.current =
      currentUserId != null
        ? String(currentUserId)
        : null
  }, [currentUserId])

  useEffect(() => {
    if (!enabled) return
    if (!token || !conversationId || !currentUserId) return

    const pusher = ensurePusher(token)

    if (!pusher) return

    // ---------------------------------------------
    // CHAT USER CHANNEL
    //
    // Backend:
    //   PrivateChannel('chat.user.{viewerId}')
    //
    // Pusher:
    //   private-chat.user.{viewerId}
    //
    // chatService OWNS this channel.
    // ---------------------------------------------
    const userChatChannelName =
      `private-chat.user.${currentUserId}`

    const existingUserChatChannel =
      pusher.channel(userChatChannelName) as
        | Channel
        | undefined

    const userChatChannel =
      existingUserChatChannel ??
      pusher.subscribe(userChatChannelName)

    channelRef.current = userChatChannel

    const handleNewMessage = (payload: any) => {
      const msg = payload?.message

      if (!msg) return

      if (
        String(msg?.conversation_id ?? '') !==
        String(conversationId)
      ) {
        return
      }

      const senderId = getSenderId(
        payload,
        msg
      )

      if (
        senderId &&
        currentUserIdRef.current &&
        senderId === currentUserIdRef.current
      ) {
        return
      }

      // Don't insert directly into cache.
      // Refresh all matching conversation queries.
      queryClient.invalidateQueries(
        {
          queryKey: [
            'conversation',
            conversationId,
          ],
          exact: false,
        },
        {
          cancelRefetch: false,
        }
      )
    }

    userChatChannel.bind(
      'new-message',
      handleNewMessage
    )

    // ---------------------------------------------
    // SHARED NOTIFICATION CHANNEL
    //
    // Backend:
    //   PrivateChannel('notification.{userId}')
    //
    // Pusher:
    //   private-notification.{userId}
    //
    // IMPORTANT:
    // chatService does NOT own this channel.
    //
    // notificationService is responsible for
    // subscribing to it and keeping it alive.
    //
    // chatService only borrows the existing channel
    // to listen for message-media-finished.
    // ---------------------------------------------
    const userChannelName =
      `private-notification.${currentUserId}`

    const userChannel =
      pusher.channel(userChannelName) as
        | Channel
        | undefined
userChannel?.bind_global((eventName: string, payload: any) => {
  console.log('[PUSHER ALL]', eventName, payload)
})
    let handleMediaFinished:
      | ((payload: any) => void)
      | null = null

    if (userChannel) {
      userChannelRef.current = userChannel

      handleMediaFinished = (
        payload: any
      ) => {
        console.log(
          '[PUSHER] message-media-finished',
          payload
        )

        const convoId =
          payload?.conversation_id ??
          payload?.message?.conversation_id

        if (!convoId) return

        // Invalidate ALL variants of this
        // conversation, including search variants.
        queryClient.invalidateQueries(
          {
            queryKey: [
              'conversation',
              convoId,
            ],
            exact: false,
          },
          {
            cancelRefetch: false,
          }
        )
      }

      userChannel.bind(
        'message-media-finished',
        handleMediaFinished
      )
    } else {
      console.warn(
        '[CHAT] Shared notification channel is not currently available',
        {
          userChannelName,
          currentUserId,
          pusherState:
            pusher.connection.state,
        }
      )
    }

    // ---------------------------------------------
    // CLEANUP
    // ---------------------------------------------
    return () => {
      // -------------------------------------------
      // Chat-owned channel
      // -------------------------------------------
      try {
        userChatChannel.unbind(
          'new-message',
          handleNewMessage
        )
      } catch {}

      try {
        pusher.unsubscribe(
          userChatChannelName
        )
      } catch {}

      channelRef.current = null

      // -------------------------------------------
      // Shared notification channel
      //
      // IMPORTANT:
      // ONLY remove our own listener.
      //
      // DO NOT:
      //   pusher.unsubscribe(userChannelName)
      //
      // notificationService still owns/needs it.
      // -------------------------------------------
      try {
        if (
          userChannel &&
          handleMediaFinished
        ) {
          userChannel.unbind(
            'message-media-finished',
            handleMediaFinished
          )
        }
      } catch {}

      userChannelRef.current = null
    }
  }, [
    token,
    conversationId,
    enabled,
    queryClient,
    currentUserId,
  ])
}